import { XcoreConfig } from "@robe/core";
import { GetServerSidePropsResult, GetStaticPropsResult } from "next";
import {
  GetXcoreServer,
  GetXcoreServerContext,
  GetXcoreServerLayout,
  GetXcoreStatic,
  GetXcoreStaticContext,
  GetXcoreStaticLayout,
} from "./types";

/** Merge layout props and server side props */
export const mergeServerProps = async <T, L>(
  ctx: GetXcoreServerContext,
  layout?: GetXcoreServerLayout<L>,
  props?: GetXcoreServer<T>
): Promise<GetServerSidePropsResult<T & L>> => {
  const [a, b] = await Promise.all([layout?.(ctx), props?.(ctx)]);

  return mergeProps<T, L>(a, b);
};

/** Merge layout props and static props */
export const mergeStaticProps = async <T, L>(
  config: XcoreConfig,
  ctx: GetXcoreStaticContext,
  layout?: GetXcoreStaticLayout<L>,
  props?: GetXcoreStatic<T>
): Promise<GetStaticPropsResult<T & L>> => {
  const [a, b] = await Promise.all([layout?.(ctx), props?.(ctx)]);

  return {
    ...(mergeProps<T, L>(a, b) as GetStaticPropsResult<L & T>),
    revalidate: config?.revalidate ?? 10,
  };
};

const mergeProps = <T, L>(
  layout: L | undefined,
  result: GetStaticPropsResult<T> | GetServerSidePropsResult<T> | undefined
): GetStaticPropsResult<L & T> | GetServerSidePropsResult<L & T> => {
  if (result === undefined) {
    return { props: layout as L & T };
  }

  if (result && "props" in result) {
    if (isPromiseLike(result.props)) {
      // @ts-ignore
      return { ...result, props: result.props.then((x) => ({ ...x, layout })) };
    } else {
      return { ...result, props: { ...result.props, ...layout! } };
    }
  }

  // Result can be of type not found, redirect or resolved result
  return result;
};

const isPromiseLike = (a: any): a is PromiseLike<unknown> => "then" in a && typeof a.then === "function";
